import { useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { AlertConfigurationModule } from "../../types"
import { HttpCodes } from "@/constants/globals"
import useDialog from "@/hooks/useDialog"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { AppDispatch, RootState } from "@/store"
import {
   deleteAlertConfiguration,
   fetchAlertConfiguration,
   resetAlertConfiguration,
   updateAlertConfiguration,
} from "@/store/alertConfigurationsSlice"
import {
   AlertConfigurationForm,
   AlertConfiguration,
   AlertCategory,
} from "@/types/alertConfigurations"
import { isEmpty } from "@/utils/lodash"

export const useAlertSettingData = () => {
   const navigate = useNavigate()
   const { id } = useParams()
   const {
      isOpen: isDeleteConfirmationDialogOpen,
      openDialog,
      closeDialog,
   } = useDialog()
   const {
      isOpen: isDisableConfirmationDialogOpen,
      openDialog: openDisableDialog,
      closeDialog: closeDisableDialog,
   } = useDialog()
   const dispatch = useDispatch<AppDispatch>()
   const {
      loadingAlertConfiguration,
      errorAlertConfiguration,
      alertConfiguration,
      updatingModule,
      loadingAlertConfigurationModule,
   } = useSelector(
      ({ alertConfigurationsReducer }: RootState) => alertConfigurationsReducer
   )
   const [form, setFormState] = useFormStateHandler<AlertConfiguration>(
      {} as AlertConfiguration
   )

   const isUpdatingAlertStatus =
      loadingAlertConfigurationModule &&
      updatingModule === AlertConfigurationModule.ALARM_ENABLED

   useEffect(() => {
      async function fetchAlertConfigurationData() {
         try {
            if (!id) {
               return
            }
            await dispatch(fetchAlertConfiguration({ alertId: id })).unwrap()
         } catch (err) {
            console.error("Alert configuration data fetch failed:", err)
         }
      }

      fetchAlertConfigurationData()

      return () => {
         dispatch(resetAlertConfiguration())
      }
   }, [id, dispatch, setFormState])

   const handleSave = async (
      state: Partial<AlertConfigurationForm>,
      configurationModule: AlertConfigurationModule
   ) => {
      try {
         await dispatch(
            updateAlertConfiguration({
               alertId: id || "",
               alertData: state,
               updatingModule: configurationModule,
            })
         ).unwrap()
      } catch (err) {
         console.error(
            `Error while saving alert configuration on ${configurationModule}:`,
            err
         )
      }
   }

   const handleEnable = () => {
      handleSave({ enabled: true }, AlertConfigurationModule.ALARM_ENABLED)
   }

   const handleDisable = async () => {
      await handleSave(
         { enabled: false },
         AlertConfigurationModule.ALARM_ENABLED
      )
      closeDisableDialog()
   }

   useEffect(() => {
      if (!loadingAlertConfiguration && !isEmpty(alertConfiguration)) {
         setFormState(alertConfiguration)
      }
   }, [loadingAlertConfiguration, alertConfiguration, setFormState])

   useEffect(() => {
      return () => {
         setFormState({} as AlertConfiguration)
      }
   }, [setFormState])

   const isVitalAlert = useMemo(
      () => alertConfiguration.category === AlertCategory.VITAL,
      [alertConfiguration.category]
   )

   const systemAlertType = useMemo(() => {
      if (alertConfiguration.systemCondition) {
         return alertConfiguration.systemCondition.type
      }
      return null
   }, [alertConfiguration.systemCondition])

   const isAlertEnabled = useMemo(
      () => alertConfiguration.enabled !== false,
      [alertConfiguration.enabled]
   )

   const handleAlertConfigurationDeletion = useCallback(
      async (alertId: string) => {
         try {
            const result = await dispatch(
               deleteAlertConfiguration({ alertId })
            ).unwrap()
            if (result.status === HttpCodes.NO_CONTENT) {
               navigate(-1)
            }
         } catch (err) {
            console.error("Alert configuration deletion failed:", err)
            closeDialog()
         }
      },
      [dispatch, closeDialog, navigate]
   )

   return {
      id,
      loadingAlertConfiguration,
      errorAlertConfiguration,
      alertConfiguration,
      form,
      isVitalAlert,
      systemAlertType,
      updatingModule,
      loadingAlertConfigurationModule,
      isDeleteConfirmationDialogOpen,
      isDisableConfirmationDialogOpen,
      isAlertEnabled,
      isUpdatingAlertStatus,
      openDialog,
      closeDialog,
      openDisableDialog,
      closeDisableDialog,
      handleSave,
      handleDisable,
      handleEnable,
      handleAlertConfigurationDeletion,
   }
}
